// @ts-nocheck
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  callInterviewClaude,
  pickFemaleFirstSpeechVoice,
  translateAnswerToEnglish,
  apiFetchInit,
} from "@/legacy/helpersModule";
import { useAppState } from "@/app/state/AppStateProvider";
import { createAnswerRecorder } from "@/features/interview/audio/answerRecorder";
import { AudioLevelMeter } from "@/features/interview/components/AudioLevelMeter";
import { InterviewRulesModal } from "@/features/interview/components/InterviewRulesModal";
import {
  enterFullscreen,
  exitFullscreen,
  isFullscreen,
  isFullscreenApiSupported,
  isMobileInterviewDevice,
} from "@/shared/lib/fullscreen";

/**
 * Fully tear down a SpeechRecognition instance so Chrome releases the tab mic
 * indicator. stop()/abort() alone often leave capture active; clearing handlers
 * and both stop + abort is required on session exit (not during normal TTS mute).
 */
function destroySpeechRecognizer(rec) {
  if (!rec) return;
  const noop = () => {};
  try {
    rec.onstart = noop;
    rec.onresult = noop;
    rec.onend = noop;
    rec.onerror = noop;
    rec.onaudiostart = null;
    rec.onaudioend = null;
    rec.onnomatch = null;
    rec.onspeechstart = null;
    rec.onspeechend = null;
  } catch (_) {
    /* ignore */
  }
  try {
    rec.stop();
  } catch (_) {
    /* ignore */
  }
  try {
    rec.abort();
  } catch (_) {
    /* ignore */
  }
}

/** Length of the shared leading run of two strings (used to tell "the engine
 * revised the same utterance" from "the engine sent a new fragment"). */
function commonPrefixLength(a, b) {
  const n = Math.min(a.length, b.length);
  let i = 0;
  while (i < n && a.charCodeAt(i) === b.charCodeAt(i)) i++;
  return i;
}

export function Interview({ context, applicationId, onEnd, onAbandon }) {
  const { meta } = useAppState();
  const ttsProvider = meta?.ttsProvider || "browser";
  const audioRef = useRef(null);  // used for Sarvam MP3 playback
  // Monotonic ticket for each speak() call. audioRef alone cannot serialise
  // playback because it stays null for the whole duration of the /api/tts
  // fetch (~1.2s on remote servers, ~50ms locally), so a second speak() during
  // that window finds "nothing playing", cancels nothing, and both clips end up
  // playing on top of each other. Every speak() takes the next ticket and only
  // acts if it still holds the latest one.
  const speakSeqRef = useRef(0);
  // AbortController for the in-flight /api/tts request, so a superseded clip is
  // cancelled while still on the wire rather than after it lands.
  const ttsAbortRef = useRef(null);
  // Keeps the OS audio-output device awake between questions so the first
  // word of a server-TTS clip isn't swallowed by speaker warm-up (the
  // "question starts from the middle" bug on AI follow-ups).
  const audioCtxRef = useRef(null);
  const keepAliveSrcRef = useRef(null);
  // Small lead-in before speech so the output device is fully awake.
  const TTS_LEAD_IN_MS = 220;
  // Fallback so playback never hangs if canplaythrough never fires.
  const TTS_READY_TIMEOUT_MS = 1200;
  const RESOLVE_TIMEOUT_MS = 90000;

  // Lazily create/resume a silent AudioContext loop that keeps the audio
  // output device warm. Must be first invoked from a user gesture (Start).
  const ensureAudioWarm = useCallback(() => {
    // Phones: skip entirely. On Android the speaker and mic share one OS audio
    // pipeline, and the system speech recognizer has to open the mic alongside
    // whatever Chrome holds open. Keeping a silent output loop running for the
    // whole interview is the one audio-path difference between the interview
    // (recognizer gets a silent stream: onaudiostart fires, onsoundstart never)
    // and a bare recognizer page on the same phone, which captures fine. The
    // clipped-first-word problem this loop fixes was only ever seen on laptops.
    if (isPhoneCaptureRef.current) return;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      if (!audioCtxRef.current) {
        audioCtxRef.current = new Ctx();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") {
        ctx.resume().catch(() => {});
      }
      if (!keepAliveSrcRef.current) {
        // 1-frame silent buffer looped at zero gain — inaudible, but keeps
        // the device output active so HTMLAudio playback doesn't cold-start.
        const buffer = ctx.createBuffer(1, 1, ctx.sampleRate);
        const src = ctx.createBufferSource();
        src.buffer = buffer;
        src.loop = true;
        const gain = ctx.createGain();
        gain.gain.value = 0;
        src.connect(gain).connect(ctx.destination);
        src.start(0);
        keepAliveSrcRef.current = src;
      }
    } catch (_) {
      /* AudioContext unavailable — degrade gracefully */
    }
  }, []);
  useEffect(() => () => {
    try { keepAliveSrcRef.current?.stop?.(); } catch (_) {}
    keepAliveSrcRef.current = null;
    try { audioCtxRef.current?.close?.(); } catch (_) {}
    audioCtxRef.current = null;
  }, []);
  const getScriptedList = (scr, ph) => {
    if (!scr) return [];
    if (ph === "opening") return scr.opening || [];
    // Role phase: the backend always sends a `role` array, so it is
    // authoritative even when empty. Only fall back to the combined `questions`
    // list for genuinely legacy payloads that never had phase arrays — otherwise
    // an empty role would wrongly replay the mandatory opening/closing questions.
    if (ph === "hr") return Array.isArray(scr.role) ? scr.role : (scr.questions || []);
    if (ph === "closing") return scr.closing || [];
    return [];
  };
  const isScriptedPhase = (ph) => ph === "opening" || ph === "hr" || ph === "closing";
  const initialPhaseFromScript = (d) => {
    if ((d.opening || []).length) return "opening";
    if ((d.role || []).length) return "hr";
    if ((d.closing || []).length) return "closing";
    return "ai";
  };
  const [phase, setPhase] = useState("load");
  const [script, setScript] = useState(null);
  const MAX_AI = Math.min(30, Math.max(1, Number(script?.aiFollowUpCount) || 12));
  const AI_DIFFICULTY = ["easy", "medium", "hard"].includes(script?.aiDifficulty) ? script.aiDifficulty : "medium";
  const [phaseIdx, setPhaseIdx] = useState(0);
  const hrPayloadRef = useRef([]);
  const attemptIdRef = useRef(null);
  const [msgs, setMsgs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [aiQCount, setAiQCount] = useState(0);
  const [hist, setHist] = useState([]);
  const [ended, setEnded] = useState(false);
  const endedRef = useRef(false);
  /** Set when the candidate ends early — aborts in-flight TTS and async question flows. */
  const sessionAbortRef = useRef(false);
  const releaseMicrophoneRef = useRef(() => {});
  const stopInterviewSessionRef = useRef(() => {});
  useEffect(() => {
    endedRef.current = ended;
  }, [ended]);
  const abandonNotifiedRef = useRef(false);
  const sessionMountRef = useRef(Date.now());
  useEffect(() => {
    sessionMountRef.current = Date.now();
    abandonNotifiedRef.current = false;
    sessionAbortRef.current = false;
    hrIntroDoneRef.current = false;
    aiBootRef.current = false;
    hrTranslateCacheRef.current = {};
    setPhaseIdx(0);
    setInterviewStarted(false);
  }, [applicationId]);
  const visHideTimerRef = useRef(null);
  const applicationIdRef = useRef(applicationId);
  useEffect(() => {
    applicationIdRef.current = applicationId;
  }, [applicationId]);
  const onAbandonRef = useRef(onAbandon);
  useEffect(() => {
    onAbandonRef.current = onAbandon;
  }, [onAbandon]);
  const [supported, setSupported] = useState(true);
  const [micError, setMicError] = useState("");
  const [showText, setShowText] = useState(false);
  const [textInput, setTextInput] = useState("");
  // Request 2 — "3,2,1 → Speak now": `capturing` is true only once the speech
  // engine actually fires onstart, so we never tell the candidate to speak
  // before the mic is truly live (which was clipping their first words).
  const [capturing, setCapturing] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const countdownTimerRef = useRef(null);
  const recStartTimerRef = useRef(null);
  // Request 1 — full-screen kiosk mode + 3-strike Esc rule.
  const [kioskWarning, setKioskWarning] = useState(false);
  const [kioskStrikes, setKioskStrikes] = useState(0);
  const kioskStrikesRef = useRef(0);
  const kioskArmedRef = useRef(false);
  const kioskSelfExitRef = useRef(false);
  const KIOSK_MAX_STRIKES = 3;
  // One physical Esc press can surface as BOTH a keydown and a fullscreen-exit
  // event; collapse anything closer than this into a single strike.
  const KIOSK_STRIKE_DEDUPE_MS = 350;
  const lastKioskStrikeAtRef = useRef(0);
  // Esc only counts once the candidate has actually started the interview.
  const interviewStartedRef = useRef(false);
  const voicesRef = useRef([]);
  const pinnedVoiceRef = useRef(null);
  const recRef = useRef(null);
  const txRef = useRef("");
  const transcriptDisplayRef = useRef("");
  const spokenAnswerCommittedRef = useRef(false);
  const suppressSpeechFinalizeRef = useRef(false);
  const busyRef = useRef(false);
  const dispatchAnswerRef = useRef(async (_t) => {});
  const finalizeSpokenAnswerRef = useRef(() => {});
  const SPEECH_FINALIZE_MS = 220;
  const SILENCE_SUBMIT_MS = 3000;
  // After the bot finishes speaking, wait a beat before opening the mic so any
  // trailing TTS audio is fully silent and can't be captured as the answer.
  const POST_SPEAK_SETTLE_MS = 350;
  // "Start answering in 3, 2, 1" cue shown before each answer. Long enough to
  // cover the speech engine warm-up so the first words are captured.
  const ANSWER_COUNTDOWN_STEPS = 3;
  const ANSWER_COUNTDOWN_TICK_MS = 700;
  // Hard cap a single captured answer so a runaway/echoing STT session can't
  // bloat the payload (mirrors the backend answer_text bound).
  const MAX_ANSWER_CHARS = 20000;
  const capAnswer = (s) =>
    s && s.length > MAX_ANSWER_CHARS ? s.slice(0, MAX_ANSWER_CHARS) : s;
  const aiBootRef = useRef(false);
  const hrIntroDoneRef = useRef(false);
  const [interviewStarted, setInterviewStarted] = useState(false);
  /* Interview conduct rules, accepted once per role before the first attempt. */
  const [showRules, setShowRules] = useState(false);
  const [rulesBusy, setRulesBusy] = useState(false);
  const [rulesError, setRulesError] = useState("");
  const [rulesAcked, setRulesAcked] = useState(false);
  const rulesAlreadyAcked = rulesAcked || Boolean(context.rulesAcknowledged);
  const [mobileFsActive, setMobileFsActive] = useState(false);
  const [tabReturnNotice, setTabReturnNotice] = useState(false);
  const tabReturnTimerRef = useRef(null);
  useEffect(() => {
    interviewStartedRef.current = interviewStarted;
  }, [interviewStarted]);
  const autoListenActiveRef = useRef(false);
  const hadSpeechRef = useRef(false);
  const silenceTimerRef = useRef(null);
  const listeningRef = useRef(false);
  /** True only when silence timer called rec.stop(); onend should finalize, not restart. */
  const intentionalStopRef = useRef(false);
  /** Prefix text before a mid-answer recognition restart (browser session limit). */
  const accumulatedAnswerRef = useRef("");
  /** Mobile STT: finals accumulated within the current recognition session. */
  const sessionFinalRef = useRef("");
  const isMobileSttRef = useRef(isMobileInterviewDevice());
  /* Which devices get the NEW capture engine. Deliberately NOT isMobileSttRef:
   * isMobileInterviewDevice() also returns true for any touch screen narrower
   * than 1024px, which includes touch laptops and a half-width window on a
   * touch monitor. Those are desktops and must keep the Web Speech path,
   * ensureAudioWarm and the existing UI exactly as they are. isMobileSttRef
   * still guards the pre-existing mobile tweaks so nothing that already
   * shipped changes behaviour. */
  const isPhoneCaptureRef = useRef(
    typeof navigator !== "undefined" &&
      /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || ""),
  );
  /* ── Phone answer capture ──────────────────────────────────────────────────
   * Phones record the answer themselves and have it transcribed server-side
   * (Sarvam) instead of using the browser recognizer: on Android the system
   * speech service competes with Chrome for the microphone and is handed
   * silence, and iOS Safari's implementation is unreliable. Everything below is
   * gated on isMobileSttRef — desktop keeps the Web Speech path untouched. */
  const answerRecRef = useRef(null);
  const recUnsupportedRef = useRef(false);
  const [recStatus, setRecStatus] = useState("idle");
  const [recLevel, setRecLevel] = useState(0);
  const [recSpeaking, setRecSpeaking] = useState(false);
  /** Non-blocking warning about lost audio. Kept out of micError, which the
   *  answer flow clears and which drives the destructive "Retry answer". */
  const [partialNotice, setPartialNotice] = useState("");
  /** questionKey of an answer interrupted mid-way, so a Retry resumes it
   *  (keeping the text already transcribed) instead of starting over. */
  const recResumeKeyRef = useRef(null);
  /** seq → transcript, so partial text can stream in while uploads drain. */
  const recPartsRef = useRef(new Map());
  const joinRecParts = () =>
    [...recPartsRef.current.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([, t]) => t)
      .filter(Boolean)
      .join(" ")
      .trim();
  const currentQuestionKey = () =>
    String(isScriptedPhase(phase) ? `${phase}:${phaseIdx}` : `ai:${aiQCount}`).slice(0, 64);
  const currentQuestionKeyRef = useRef(currentQuestionKey());
  currentQuestionKeyRef.current = currentQuestionKey();
  const kioskSupportedRef = useRef(isFullscreenApiSupported());
  /* ── Opt-in interview diagnostics (grep DBG-b7c1). Off by default; open the
   * app with ?dbg=on to get a timestamped event log panel at the bottom of the
   * interview screen with a Copy button — used to debug phone voice capture
   * without a desktop attached. main.tsx keeps the flag across navigation. */
  const dbgFlagsRef = useRef((() => {
    try {
      // URL wins if present; otherwise whatever main.tsx stashed at app entry
      // (the SPA drops the query string on client-side navigation). ?dbg=off clears.
      const fromUrl = new URLSearchParams(window.location.search).get("dbg");
      const q = fromUrl != null ? fromUrl : (sessionStorage.getItem("dbg_b7c1") || "");
      if (q === "off") { sessionStorage.removeItem("dbg_b7c1"); return new Set(); }
      return new Set(q.split(",").map((s) => s.trim()).filter(Boolean));
    } catch (_) { return new Set(); }
  })());
  const dbgOn = dbgFlagsRef.current.size > 0;
  const dbgT0Ref = useRef(performance.now());
  const [dbgLines, setDbgLines] = useState([]);
  const dbg = (event, extra) => {
    if (!dbgOn) return;
    const t = ((performance.now() - dbgT0Ref.current) / 1000).toFixed(2);
    const line = `+${t}s ${event}` + (extra === undefined ? "" : " " + JSON.stringify(extra));
    try { console.log(`[DBG-b7c1] ${line}`); } catch (_) {}
    setDbgLines((p) => (p.length > 120 ? [...p.slice(-90), line] : [...p, line]));
  };
  const dbgRef = useRef(dbg);
  dbgRef.current = dbg;
  // Everything about the audio session at one instant — logged right before start().
  const dbgSnapshot = () => ({
    fs: isFullscreen(),
    actx: audioCtxRef.current ? audioCtxRef.current.state : "none",
    vis: document.visibilityState,
    focus: document.hasFocus(),
    synth: Boolean(window.speechSynthesis?.speaking),
    audioEl: audioRef.current ? "alive" : "none",
    flags: [...dbgFlagsRef.current].join("|") || "-",
  });
  /* ── end diagnostics setup ── */
  const VISIBILITY_ABANDON_MS = isMobileSttRef.current ? 75000 : 12000;
  const hrTranslateCacheRef = useRef({});
  const startAutoListenAfterQuestionRef = useRef(() => {});
  const langMap = { English: "en-IN", Hindi: "hi-IN", Bengali: "bn-IN", Tamil: "ta-IN", Telugu: "te-IN", Kannada: "kn-IN", Marathi: "mr-IN", Gujarati: "gu-IN", Malayalam: "ml-IN" };
  const langCode = langMap[context.language] || "en-IN";
  const buildSysAi = (scr) => {
    const topics = (scr?.doNotRepeatTopics || []).slice(0, 30).map((t, i) => (i + 1) + ". " + t).join("\n");
    const desc = scr?.jobDescription || context.jd?.description || "";
    const req = scr?.jobRequirements || context.jd?.requirements || "";
    const difficulty = ["easy", "medium", "hard"].includes(scr?.aiDifficulty) ? scr.aiDifficulty : AI_DIFFICULTY;
    const total = Math.min(30, Math.max(1, Number(scr?.aiFollowUpCount) || MAX_AI));
    const difficultyBlock = {
      easy: "Difficulty: EASY. Ask straightforward role basics — definitions, daily duties, fundamental tools, simple scenarios. Keep questions accessible to candidates with limited experience. Avoid multi-step problems and edge cases.",
      medium: "Difficulty: MEDIUM. Ask standard role competency questions — situational judgement, moderate-complexity scenarios, single-step problem solving, common pitfalls. Probe both knowledge and applied judgement.",
      hard: "Difficulty: HARD. Ask senior-level scenario-based questions — multi-step problems, edge cases, deep technical probing, behavioural STAR with complications. Press on trade-offs, decision-making under constraints, and ownership.",
    }[difficulty];
    return `You are AIRA, an HR interviewer. Voice interview for "${context.jd.title}" with ${context.candidateName}. Speak in ${context.language}. <30 words per turn. NO markdown. Ask ONE role-specific question per turn about skills, experience, or scenarios for THIS job only.

Output ONLY the question itself. Do NOT greet, introduce yourself, welcome, thank, or add any preamble or transition phrase. No "Let's begin", no "Great answer", no commentary — just the question.

${difficultyBlock}

You will ask ${total} role-specific follow-up questions in total. Cover DISTINCT facets each turn (e.g. technical depth, hands-on tools, scenario judgement, collaboration, problem-solving under constraints, role-specific edge cases, behaviour under pressure, communication with stakeholders, ownership and trade-offs). Do not cluster on one topic; rotate facets across turns and go progressively deeper.

Already asked (NEVER repeat or rephrase):
${topics || "(none)"}

Job description: ${desc}
Requirements: ${req}

Do NOT ask: introduce yourself, tell me about yourself, why this company, name confirmation, or any topic already covered above.
After exactly ${total} follow-up questions, output ONLY "[INTERVIEW_COMPLETE]" with no other words, no goodbye, no summary, no thanks.`;
  };
  const translateHrScriptLine = useCallback(
    async (raw) => {
      const t = (raw == null ? "" : String(raw)).trim();
      if (!t) return t;
      const target = (context.language || "English").trim();
      if (/^english$/i.test(target)) return t;
      if (typeof window !== "undefined" && !window.CLAUDE_API_URL) return t;
      const key = target + "\n" + t;
      const c = hrTranslateCacheRef.current;
      if (c[key]) return c[key];
      const system = `You translate job interview questions for text-to-speech. Output ONLY the translated question in ${target}. No title, no quotes, no explanation, no markdown. Preserve proper names, product names, and acronyms where natural in ${target}. If the text is already in ${target}, return it unchanged.`;
      try {
        const out = await callInterviewClaude(
          [{ role: "user", content: "Translate this interview question:\n\n" + t }],
          system,
          applicationId,
        );
        const cleaned = (out || "").replace(/```[a-z]*\n?/gi, "").replace(/```/g, "").trim() || t;
        c[key] = cleaned;
        return cleaned;
      } catch (e) {
        return t;
      }
    },
    [context.language],
  );
  const resolveAnswerFields = useCallback(
    async (answer) => {
      const lang = (context.language || "English").trim();
      if (/^english$/i.test(lang)) {
        return { answerText: answer, answerTextEn: null };
      }
      const en = await translateAnswerToEnglish(answer, lang, applicationId);
      return { answerText: answer, answerTextEn: en || null };
    },
    [context.language, applicationId],
  );
  const safeAbandon = async (detail) => {
    if (abandonNotifiedRef.current || !applicationId) return;
    stopInterviewSessionRef.current();
    const fn = onAbandonRef.current;
    if (typeof fn !== "function") return;
    abandonNotifiedRef.current = true;
    try {
      await fn(detail || "");
    } catch (_) {}
  };
  useEffect(() => {
    const h = () => {
      if (endedRef.current || !applicationId) return;
      void safeAbandon("page_leave");
    };
    window.addEventListener("pagehide", h);
    return () => window.removeEventListener("pagehide", h);
  }, [applicationId]);
  useEffect(() => {
    const h = () => {
      if (endedRef.current || !applicationId) return;
      void safeAbandon("network_offline");
    };
    window.addEventListener("offline", h);
    return () => window.removeEventListener("offline", h);
  }, [applicationId]);

  /* ── Full-screen "kiosk" mode (Request 1) ─────────────────────────────────
   * The candidate enters full-screen when they start the interview. Browsers
   * never let us block Esc, so instead we detect exits: the first two count as
   * warnings (with a "Resume" button), and the third ends the interview as
   * incomplete so the candidate can request a re-attempt. This is layered ON
   * TOP of the existing 12-second tab-away guard (Option A), not a replacement.
   */
  const resumeKiosk = async () => {
    // Keep the overlay up until we're genuinely back in full-screen; the
    // fullscreenchange handler also clears it on success.
    const ok = await enterFullscreen();
    if (ok) setKioskWarning(false);
  };
  // A "strike" = the candidate left the locked full-screen interview. We accept
  // BOTH signals that indicate this — the Escape keydown AND the fullscreen-exit
  // (fullscreenchange) — because browsers are inconsistent: some swallow the Esc
  // keydown when it's used to leave full-screen, and an F11/OS full-screen never
  // fires fullscreenchange. Counting both makes the 3-strike rule fire reliably
  // everywhere; the dedupe window collapses the two events from a single physical
  // Esc press into one strike. Gated on interviewStartedRef so Esc before the
  // interview begins is ignored.
  const registerKioskStrike = () => {
    if (endedRef.current) return;
    if (!interviewStartedRef.current) return;
    // iOS Safari and similar browsers cannot enter page fullscreen — skip strikes.
    if (isMobileSttRef.current && !kioskSupportedRef.current) return;
    const now = Date.now();
    if (now - lastKioskStrikeAtRef.current < KIOSK_STRIKE_DEDUPE_MS) return;
    lastKioskStrikeAtRef.current = now;
    const n = (kioskStrikesRef.current || 0) + 1;
    kioskStrikesRef.current = n;
    setKioskStrikes(n);
    if (n >= KIOSK_MAX_STRIKES) {
      setKioskWarning(false);
      stopInterviewSessionRef.current();
      void safeAbandon("fullscreen_exit_limit");
    } else {
      setKioskWarning(true);
    }
  };
  useEffect(() => {
    const onFsChange = () => {
      if (isFullscreen()) {
        // Entered full-screen — arm the guard and clear any stale warning.
        kioskArmedRef.current = true;
        setKioskWarning(false);
        if (isMobileSttRef.current) setMobileFsActive(true);
        return;
      }
      if (isMobileSttRef.current) setMobileFsActive(false);
      // Left full-screen.
      if (kioskSelfExitRef.current) { kioskSelfExitRef.current = false; return; }
      if (!kioskArmedRef.current) return; // never entered — ignore
      if (endedRef.current) return; // interview already finished
      registerKioskStrike();
    };
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("webkitfullscreenchange", onFsChange);
    // If we mounted already in full-screen (entered via the Start gesture on the
    // previous screen), arm immediately so the first Esc still counts.
    if (isFullscreen()) {
      kioskArmedRef.current = true;
      if (isMobileSttRef.current) setMobileFsActive(true);
    }
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("webkitfullscreenchange", onFsChange);
    };
  }, []);
  // Count the Escape key directly too. This is the reliable signal when the
  // browser doesn't fire fullscreenchange (e.g. F11/OS full-screen, or when
  // full-screen never engaged). Deduped with the fullscreen-exit path above so a
  // single Esc press is one strike, and gated to the live interview only.
  useEffect(() => {
    const onEscKey = (e) => {
      if (e.key !== "Escape" && e.key !== "Esc") return;
      if (!interviewStartedRef.current || endedRef.current) return;
      registerKioskStrike();
    };
    document.addEventListener("keydown", onEscKey, true);
    return () => document.removeEventListener("keydown", onEscKey, true);
  }, []);
  // Leave full-screen cleanly whenever the interview finishes or unmounts, so we
  // never trip a false "exit" strike on the way out.
  useEffect(() => {
    if (!ended) return;
    releaseMicrophoneRef.current();
    kioskSelfExitRef.current = true;
    void exitFullscreen();
  }, [ended]);
  useEffect(
    () => () => {
      releaseMicrophoneRef.current();
      kioskSelfExitRef.current = true;
      void exitFullscreen();
    },
    [],
  );
  /* ── Interview lockdown ────────────────────────────────────────────────────
   * While the interview screen is mounted, block copy / cut / paste and deter
   * screenshot / right-click / DevTools shortcuts. Typing in the answer box is
   * still allowed; paste and copy are blocked everywhere (questions + answers).
   * NOTE: OS-level screenshots and DevTools cannot be fully blocked by any web
   * page — this is a deterrent layered on top of the full-screen kiosk mode.
   */
  useEffect(() => {
    const onContextMenu = (e) => {
      e.preventDefault();
    };
    const onCopyCutPaste = (e) => {
      e.preventDefault();
    };
    const onKeyDown = (e) => {
      const k = (e.key || "").toLowerCase();
      if (e.key === "PrintScreen") {
        // Best-effort: wipe whatever the screenshot may have placed on the clipboard.
        try { navigator.clipboard?.writeText?.(""); } catch (_) {}
        return;
      }
      const isDevtools =
        e.key === "F12" ||
        ((e.ctrlKey || e.metaKey) && e.shiftKey && (k === "i" || k === "j" || k === "c")) ||
        ((e.ctrlKey || e.metaKey) && k === "u");
      if (isDevtools) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      // Copy / cut / paste / save / print / select-all — blocked everywhere.
      const isClipboard =
        (e.ctrlKey || e.metaKey) && (k === "c" || k === "x" || k === "v" || k === "s" || k === "p" || k === "a");
      if (isClipboard) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener("contextmenu", onContextMenu, true);
    document.addEventListener("copy", onCopyCutPaste, true);
    document.addEventListener("cut", onCopyCutPaste, true);
    document.addEventListener("paste", onCopyCutPaste, true);
    document.addEventListener("keydown", onKeyDown, true);
    const prevUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = "none";
    return () => {
      document.removeEventListener("contextmenu", onContextMenu, true);
      document.removeEventListener("copy", onCopyCutPaste, true);
      document.removeEventListener("cut", onCopyCutPaste, true);
      document.removeEventListener("paste", onCopyCutPaste, true);
      document.removeEventListener("keydown", onKeyDown, true);
      document.body.style.userSelect = prevUserSelect;
    };
  }, []);
  /* ── Mobile-only screenshot deterrence (fullscreen interview) ───────────────
   * OS screenshot buttons cannot be blocked in mobile browsers; this adds
   * touch-callout disable, clipboard wipe on hide, and a watermark overlay.
   * Desktop/laptop lockdown above is unchanged.
   */
  useEffect(() => {
    if (!isMobileSttRef.current || !interviewStarted) return;

    const body = document.body;
    const prevTouchCallout = body.style.webkitTouchCallout;
    const prevWebkitUserSelect = body.style.webkitUserSelect;
    body.style.webkitTouchCallout = "none";
    body.style.webkitUserSelect = "none";

    const onVisibility = () => {
      if (!interviewStartedRef.current || endedRef.current) return;
      if (document.visibilityState === "hidden") {
        try {
          // DBG-b7c1: don't wipe the clipboard while diagnosing, or the copied
          // log is gone the moment the candidate switches apps to send it.
          if (dbgOn) return;
          void navigator.clipboard?.writeText?.("");
        } catch (_) {
          /* best-effort */
        }
      }
    };

    document.addEventListener("visibilitychange", onVisibility, true);

    return () => {
      body.style.webkitTouchCallout = prevTouchCallout;
      body.style.webkitUserSelect = prevWebkitUserSelect;
      document.removeEventListener("visibilitychange", onVisibility, true);
    };
  }, [interviewStarted]);
  useEffect(() => {
    if (!interviewStarted || ended) return undefined;
    let wakeLock = null;
    const acquire = async () => {
      try {
        if ("wakeLock" in navigator) {
          wakeLock = await navigator.wakeLock.request("screen");
        }
      } catch (_) {
        /* unsupported or denied — interview continues */
      }
    };
    void acquire();
    const onVis = () => {
      if (document.visibilityState === "visible") void acquire();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      void wakeLock?.release?.();
    };
  }, [interviewStarted, ended]);
  useEffect(() => {
    const clearT = () => {
      if (visHideTimerRef.current) {
        clearTimeout(visHideTimerRef.current);
        visHideTimerRef.current = null;
      }
    };
    const clearReturnNotice = () => {
      if (tabReturnTimerRef.current) {
        clearTimeout(tabReturnTimerRef.current);
        tabReturnTimerRef.current = null;
      }
    };
    const onVis = () => {
      clearT();
      if (endedRef.current || !applicationId) return;
      if (document.visibilityState === "visible") {
        if (interviewStartedRef.current && !endedRef.current) {
          setTabReturnNotice(true);
          clearReturnNotice();
          tabReturnTimerRef.current = setTimeout(() => {
            tabReturnTimerRef.current = null;
            setTabReturnNotice(false);
          }, 4000);
        }
        return;
      }
      if (document.visibilityState === "hidden") {
        const aidSnapshot = applicationIdRef.current;
        visHideTimerRef.current = setTimeout(() => {
          visHideTimerRef.current = null;
          if (document.visibilityState !== "hidden") return;
          if (endedRef.current || !aidSnapshot) return;
          if (applicationIdRef.current !== aidSnapshot) return;
          void safeAbandon("visibility_hidden");
        }, VISIBILITY_ABANDON_MS);
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearT();
      clearReturnNotice();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [applicationId, VISIBILITY_ABANDON_MS]);
  useEffect(() => {
    return () => {
      if (!applicationId) return;
      if (Date.now() - sessionMountRef.current < 800) return;
      if (endedRef.current) return;
      void safeAbandon("component_unmount");
    };
  }, [applicationId]);
  useEffect(() => { const load = () => { voicesRef.current = window.speechSynthesis?.getVoices?.() || []; }; load(); if (window.speechSynthesis) window.speechSynthesis.onvoiceschanged = load; }, []);
  useEffect(() => {
    busyRef.current = busy;
  }, [busy]);
  useEffect(() => {
    listeningRef.current = listening;
  }, [listening]);
  // Phones only: build the record-and-upload recorder once per mount. Callbacks
  // go through refs (dispatchAnswerRef etc.) so this never needs re-creating.
  useEffect(() => {
    if (!isPhoneCaptureRef.current) return undefined;
    const rec = createAnswerRecorder({
      onStatus: (s) => {
        setRecStatus(s);
        // Only "recording" means the mic is live. During "finishing" the mic is
        // already released and uploads are draining, so the meter and the Done
        // button must go; `listening` stays true to keep the answer panel up.
        setCapturing(s === "recording");
        if (s !== "recording") {
          setRecLevel(0);
          setRecSpeaking(false);
        }
      },
      onLevel: (level01, speakingNow) => {
        setRecLevel(level01);
        setRecSpeaking(Boolean(speakingNow));
      },
      onSpeechStart: () => {
        hadSpeechRef.current = true;
        setMicError("");
      },
      onNoSpeechHint: () => {
        if (!hadSpeechRef.current) setMicError("We can't hear you — hold the phone closer and keep speaking.");
      },
      onSegmentUploaded: (seq, text) => {
        recPartsRef.current.set(seq, text || "");
        const joined = capAnswer(joinRecParts());
        txRef.current = joined;
        transcriptDisplayRef.current = joined;
        setTranscript(joined);
      },
      onFinal: ({ transcript: finalText, failedSegments }) => {
        setRecLevel(0);
        setRecSpeaking(false);
        setListening(false);
        setCapturing(false);
        autoListenActiveRef.current = false;
        recResumeKeyRef.current = null;
        const answer = capAnswer(String(finalText || "").trim());
        if (!answer) {
          setMicError("No speech detected — please speak again.");
          return;
        }
        if (spokenAnswerCommittedRef.current) return;
        spokenAnswerCommittedRef.current = true;
        // Warn AFTER dispatch: dispatchAnswer clears micError synchronously, and
        // partialNotice is separate so it survives into the next question.
        setPartialNotice(failedSegments > 0 ? "Part of that answer could not be sent." : "");
        void dispatchAnswerRef.current(answer);
      },
      onError: (e) => {
        // A permanently failed segment upload is a warning, NOT the end of the
        // answer: the recorder keeps capturing and will still emit onFinal, so
        // tearing the UI down here would strand a live microphone behind a
        // "Retry" button and lose the rest of the answer.
        if (e.code === "upload-failed") {
          setPartialNotice("Part of your answer may not have sent — keep going.");
          return;
        }
        setRecLevel(0);
        setRecSpeaking(false);
        setListening(false);
        setCapturing(false);
        autoListenActiveRef.current = false;
        recResumeKeyRef.current = e.code === "interrupted" ? currentQuestionKeyRef.current : null;
        if (e.code === "not-allowed") setMicError("Microphone blocked — allow it in the address bar, then tap Retry.");
        else if (e.code === "not-readable") setMicError("Microphone is busy — close other apps, then tap Retry.");
        else if (e.code === "interrupted") setMicError("Recording paused — tap Retry to carry on answering.");
        else if (e.code === "unsupported") { recUnsupportedRef.current = true; setShowText(true); }
        else setMicError("We couldn't send that answer — tap Retry, or type it instead.");
      },
      onDebug: (event, extra) => dbgRef.current(event, extra),
    });
    answerRecRef.current = rec;
    if (!rec.isSupported()) {
      recUnsupportedRef.current = true;
      setShowText(true);
    }
    return () => {
      rec.release();
      answerRecRef.current = null;
    };
  }, []);
  // Microphone permission + quality is now handled by the <MicCheck> pre-flight
  // on the Start-interview screen (single prompt), so no silent mount-time grab.
  useEffect(() => {
    pinnedVoiceRef.current = null;
    // Phones never construct a recognizer: Web Speech and a live getUserMedia
    // track cannot coexist on Android, and the recorder above owns the mic.
    if (isPhoneCaptureRef.current) return undefined;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setSupported(false); setShowText(true); return; }
    const rec = new SR();
    rec.continuous = true; rec.interimResults = true; rec.lang = langCode;
    // Phone-only: (re)arm the trailing-silence auto-submit. Desktop arms it
    // inside onresult (unchanged below); Android also needs it at the START of
    // a restarted session, because the engine ends a session at every pause and
    // a silent follow-up session never delivers a result to arm it.
    const armSilenceTimerMobile = () => {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = setTimeout(() => {
        if (!autoListenActiveRef.current || !listeningRef.current) return;
        if (!hadSpeechRef.current) return;
        intentionalStopRef.current = true;
        autoListenActiveRef.current = false;
        try { rec.stop(); } catch (_) {}
      }, SILENCE_SUBMIT_MS);
    };
    // Phone-only: Android reports a silent session as `nomatch` (not a
    // `no-speech` error) and then ends it. Once the candidate has spoken, that
    // silence IS the end of the answer — stop intentionally so onend finalizes
    // instead of restarting the engine indefinitely.
    rec.onnomatch = () => {
      if (!isMobileSttRef.current) return;
      if (!autoListenActiveRef.current || !hadSpeechRef.current) return;
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
      intentionalStopRef.current = true;
      autoListenActiveRef.current = false;
    };
    // Fires when the engine is genuinely capturing audio — the only safe moment
    // to tell the candidate "Speak now".
    rec.onstart = () => {
      setCapturing(true);
      if (isMobileSttRef.current) {
        sessionFinalRef.current = "";
        // A session restarted after speech: count the silence from now.
        if (autoListenActiveRef.current && hadSpeechRef.current) armSilenceTimerMobile();
      }
    };
    rec.onresult = (e) => {
      const prefix = accumulatedAnswerRef.current ? accumulatedAnswerRef.current + " " : "";

      if (isMobileSttRef.current) {
        // Android Chrome reports every update as the WHOLE utterance so far —
        // cumulative, and flagged final — not as a new fragment. Appending them
        // produced "mymy distancemy distance". Within one engine session the
        // latest result therefore REPLACES the session text; text from earlier
        // sessions (the engine ends one at every pause and onend restarts it)
        // arrives via accumulatedAnswerRef as the prefix. The prefix checks keep
        // a device that genuinely emits fragments working as well.
        const last = e.results[e.results.length - 1];
        const lastText = String(last?.[0]?.transcript || "").trim();
        const prev = sessionFinalRef.current.trim();
        let sessionText = prev;
        if (lastText) {
          if (!prev || lastText.startsWith(prev)) sessionText = lastText;
          else if (prev.startsWith(lastText)) sessionText = prev;
          else if (commonPrefixLength(prev, lastText) >= Math.ceil(prev.length / 2)) sessionText = lastText; // same utterance, revised
          else sessionText = prev + " " + lastText; // a genuine new fragment
        }
        sessionFinalRef.current = sessionText;
        const committed = capAnswer((prefix + sessionText).trim());
        txRef.current = committed;
        transcriptDisplayRef.current = committed;
        setTranscript(committed);
        if (sessionText) {
          hadSpeechRef.current = true;
          setMicError("");
        }
      } else {
        // Desktop/laptop — unchanged path.
        let finalT = "";
        let interimT = "";
        for (let i = 0; i < e.results.length; i++) {
          const seg = e.results[i][0]?.transcript || "";
          if (e.results[i].isFinal) finalT += seg;
          else interimT += seg;
        }
        const committed = capAnswer((prefix + finalT).trim());
        const display = capAnswer((prefix + finalT + (interimT ? " " + interimT : "")).trim());
        txRef.current = committed;
        transcriptDisplayRef.current = display;
        setTranscript(display);
        if (committed || interimT) {
          hadSpeechRef.current = true;
          setMicError("");
        }
      }

      if (autoListenActiveRef.current) {
        clearTimeout(silenceTimerRef.current);
        silenceTimerRef.current = setTimeout(() => {
          if (!autoListenActiveRef.current || !listeningRef.current) return;
          if (!hadSpeechRef.current) return;
          intentionalStopRef.current = true;
          autoListenActiveRef.current = false;
          try { rec.stop(); } catch (_) {}
        }, SILENCE_SUBMIT_MS);
      }
    };
    rec.onend = () => {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
      if (suppressSpeechFinalizeRef.current || endedRef.current || sessionAbortRef.current) {
        setListening(false);
        setCapturing(false);
        autoListenActiveRef.current = false;
        intentionalStopRef.current = false;
        return;
      }
      if (autoListenActiveRef.current && !intentionalStopRef.current && !spokenAnswerCommittedRef.current) {
        const full = (txRef.current || "").trim();
        if (full) {
          accumulatedAnswerRef.current = full;
          transcriptDisplayRef.current = full;
          hadSpeechRef.current = true;
        }
        if (isMobileSttRef.current) sessionFinalRef.current = "";
        try {
          rec.start();
          setListening(true);
          return;
        } catch (_) {
          setListening(false);
          autoListenActiveRef.current = false;
          intentionalStopRef.current = false;
          setTimeout(() => finalizeSpokenAnswerRef.current(), SPEECH_FINALIZE_MS);
          return;
        }
      }
      intentionalStopRef.current = false;
      setListening(false);
      setCapturing(false);
      autoListenActiveRef.current = false;
      setTimeout(() => finalizeSpokenAnswerRef.current(), SPEECH_FINALIZE_MS);
    };
    rec.onerror = (e) => {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
      setListening(false);
      setCapturing(false);
      autoListenActiveRef.current = false;
      intentionalStopRef.current = false;
      if (e.error === "not-allowed") setMicError("Mic denied.");
      else if (e.error === "no-speech") {
        if (hadSpeechRef.current || (txRef.current || "").trim() || (transcriptDisplayRef.current || "").trim()) return;
        setMicError("No speech detected — please speak again.");
      } else if (e.error !== "aborted") setMicError("Speech error.");
    };
    recRef.current = rec;
    return () => {
      // Exit / lang remount: release via ref, then ensure this effect's instance
      // is destroyed even if the ref was cleared elsewhere.
      releaseMicrophoneRef.current();
      destroySpeechRecognizer(rec);
      if (recRef.current === rec) recRef.current = null;
    };
  }, [langCode]);
  /**
   * Hard-stop every server-TTS source: the request still on the wire, and the
   * clip already playing. Bumping the ticket makes any fetch that resolves
   * after this point discard itself instead of starting playback.
   */
  const stopServerTts = () => {
    speakSeqRef.current += 1;
    if (ttsAbortRef.current) {
      try { ttsAbortRef.current.abort(); } catch (_) {}
      ttsAbortRef.current = null;
    }
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch (_) {}
      audioRef.current = null;
    }
  };

  const speak = (text) => new Promise((resolve) => {
    if (!text || sessionAbortRef.current || endedRef.current) { resolve(); return; }

    // Mute the mic for the whole time the bot is speaking so its own TTS audio
    // cannot be captured as the candidate's answer (acoustic echo).
    autoListenActiveRef.current = false;
    clearTimeout(silenceTimerRef.current);
    silenceTimerRef.current = null;
    if (isPhoneCaptureRef.current) {
      // Half-duplex: the phone must not hold the microphone while the question
      // plays — Android silences one of two concurrent captures, and iOS routes
      // playback to the earpiece whenever a mic stream is open.
      suppressSpeechFinalizeRef.current = true;
      setListening(false);
      setCapturing(false);
      recResumeKeyRef.current = null;
      answerRecRef.current?.cancel();
    } else if (listeningRef.current && recRef.current) {
      suppressSpeechFinalizeRef.current = true;
      setListening(false);
      try { recRef.current.abort(); } catch (_) {}
    }

    // ── Server TTS path (Sarvam) ─────────────────────────────────────────────
    if (ttsProvider !== "browser") {
      setSpeaking(true);
      // Stop any current server-TTS audio AND any request still in flight, then
      // claim the ticket this call must keep hold of to be allowed to play.
      stopServerTts();
      const mySeq = speakSeqRef.current;
      const isStale = () => speakSeqRef.current !== mySeq;
      const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
      ttsAbortRef.current = controller;
      // Send the full BCP-47 code (e.g. "hi-IN"); the backend maps it per provider.
      const init = apiFetchInit({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: text.trim(), language: langCode || "en-IN" }),
      });
      if (controller) init.signal = controller.signal;
      fetch("/api/tts", init)
        .then((r) => {
          if (isStale()) { resolve(); return null; }
          if (sessionAbortRef.current || endedRef.current) { resolve(); return null; }
          if (!r.ok) throw new Error(`TTS HTTP ${r.status}`);
          return r.blob();
        })
        .then((blob) => {
          if (ttsAbortRef.current === controller) ttsAbortRef.current = null;
          if (isStale()) { resolve(); return; }
          if (!blob || sessionAbortRef.current || endedRef.current) { resolve(); return; }
          const url = URL.createObjectURL(blob);
          const audio = new Audio(url);
          audio.preload = "auto";
          audio.playsInline = true;
          audio.setAttribute("playsinline", "");
          audio.setAttribute("webkit-playsinline", "");
          audioRef.current = audio;
          // Only clear audioRef if this clip still owns it — a superseded clip
          // must never null out the ref belonging to the one now playing.
          const release = () => {
            URL.revokeObjectURL(url);
            if (audioRef.current === audio) audioRef.current = null;
            if (!isStale()) setSpeaking(false);
            resolve();
          };
          audio.onended = release;
          audio.onerror = release;
          // Fix for AI follow-up clipping: don't call play() until the clip is
          // actually decodable, keep the output device warm, and give the
          // hardware a short lead-in so the first word is never swallowed.
          let started = false;
          const startPlayback = () => {
            if (started || audioRef.current !== audio) return;
            if (isStale() || sessionAbortRef.current || endedRef.current) {
              URL.revokeObjectURL(url);
              if (audioRef.current === audio) audioRef.current = null;
              if (!isStale()) setSpeaking(false);
              resolve();
              return;
            }
            started = true;
            ensureAudioWarm();
            setTimeout(() => {
              if (isStale() || sessionAbortRef.current || endedRef.current || audioRef.current !== audio) {
                if (!isStale()) setSpeaking(false);
                resolve();
                return;
              }
              audio.play().catch(() => { if (!isStale()) setSpeaking(false); resolve(); });
            }, TTS_LEAD_IN_MS);
          };
          audio.oncanplaythrough = startPlayback;
          // Already buffered enough? Start immediately.
          if (audio.readyState >= 3 /* HAVE_FUTURE_DATA */) startPlayback();
          // Fallback so we never hang if canplaythrough doesn't fire.
          setTimeout(startPlayback, TTS_READY_TIMEOUT_MS);
          audio.load();
        })
        .catch((err) => {
          if (ttsAbortRef.current === controller) ttsAbortRef.current = null;
          // A superseded clip aborts on purpose — that is not a failure, and it
          // must NOT fall back to the browser voice or we would re-speak stale
          // text in a different voice over the live question.
          if (isStale() || err?.name === "AbortError") { resolve(); return; }
          if (sessionAbortRef.current || endedRef.current) { resolve(); return; }
          console.warn("[tts] server TTS failed, falling back to browser TTS:", err);
          // Graceful fallback to browser TTS on error
          setSpeaking(false);
          speakBrowser(text).then(resolve);
        });
      return;
    }

    // ── Browser TTS path (default) ───────────────────────────────────────────
    speakBrowser(text).then(resolve);
  });

  // Browser Web Speech API TTS — kept separate as the fallback when Sarvam fails
  const speakBrowser = (text) => new Promise((resolve) => {
    if (!text || !window.speechSynthesis || sessionAbortRef.current || endedRef.current) {
      resolve();
      return;
    }
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = langCode;
    u.rate = 0.91;
    u.pitch = 1.02;
    // Refresh voices from browser if not yet loaded
    if (!voicesRef.current.length) {
      const fresh = window.speechSynthesis?.getVoices?.() || [];
      if (fresh.length) voicesRef.current = fresh;
    }
    // Pin the female voice on first successful pick and reuse for all questions
    if (!pinnedVoiceRef.current) {
      pinnedVoiceRef.current = pickFemaleFirstSpeechVoice(langCode, voicesRef.current) || null;
    }
    if (pinnedVoiceRef.current) u.voice = pinnedVoiceRef.current;
    u.onstart = () => { if (!sessionAbortRef.current && !endedRef.current) setSpeaking(true); };
    u.onend = () => { setSpeaking(false); resolve(); };
    u.onerror = () => { setSpeaking(false); resolve(); };
    if (sessionAbortRef.current || endedRef.current) { resolve(); return; }
    window.speechSynthesis.speak(u);
  });

  useEffect(() => {
    if (!applicationId) {
      setPhase("load");
      setScript(null);
      return;
    }
    let cancelled = false;
    setPhase("load");
    attemptIdRef.current = null;
    fetch(`/api/voice-bot/interview-script/${applicationId}`, apiFetchInit())
      .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
      .then(async (d) => {
        if (cancelled) return;
        setScript(d);
        setPhaseIdx(0);
        setPhase(initialPhaseFromScript(d));
        try {
          const sr = await fetch(
            "/api/voice-bot/interview-session-start",
            apiFetchInit({
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
              applicationId,
              language: context.language || "English",
            }),
            }),
          );
          if (sr.ok) {
            const sd = await sr.json();
            if (sd.attemptId != null) attemptIdRef.current = Number(sd.attemptId);
          }
        } catch (_) {}
      })
      .catch(() => { if (!cancelled) setPhase("err"); });
    return () => { cancelled = true; };
  }, [applicationId, context.language]);
  useEffect(() => {
    if (applicationId) return;
    const t = setTimeout(() => {
      setPhase((prev) => (prev === "load" ? "err" : prev));
    }, RESOLVE_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [applicationId]);
  useEffect(() => {
    if (!isScriptedPhase(phase) || !interviewStarted || !script) return;
    if (phaseIdx !== 0) return;
    const qs = getScriptedList(script, phase);
    if (!qs.length) return;
    let cancelled = false;
    (async () => {
      await new Promise((r) => setTimeout(r, 400));
      if (cancelled) return;
      if (hrIntroDoneRef.current) return;
      hrIntroDoneRef.current = true;
      const q0 = qs[0];
      setBusy(true);
      let toSpeak = q0.question;
      try {
        toSpeak = await translateHrScriptLine(q0.question);
      } catch (_) {}
      if (cancelled) {
        setBusy(false);
        return;
      }
      setBusy(false);
      setMsgs((p) => [...p, { role: "ai", text: toSpeak, tag: phase }]);
      await speak(toSpeak);
      if (!cancelled && !sessionAbortRef.current && !endedRef.current) {
        startAutoListenAfterQuestionRef.current();
      }
    })();
    // Stop only the clip this effect started. It must NOT abort pending /api/tts
    // work: this cleanup fires on the phaseIdx 0→1 change, by which point the
    // next question's request has already been issued by submitAnswer, and
    // cancelling it would leave that question silent. Serialising concurrent
    // speak() calls is speak()'s own job (see the ticket in stopServerTts).
    return () => { cancelled = true; if (window.speechSynthesis) window.speechSynthesis.cancel(); if (audioRef.current) { try { audioRef.current.pause(); } catch (_) {} audioRef.current = null; } };
  }, [phase, script, interviewStarted, translateHrScriptLine, phaseIdx]);
  useEffect(() => {
    if (!script || phase === "load" || phase === "err" || phase === "ai") return;
    const qs = getScriptedList(script, phase);
    if (qs.length > 0) return;
    hrIntroDoneRef.current = false;
    if (phase === "opening") { setPhaseIdx(0); setPhase("hr"); }
    // Empty role phase → go to the AI follow-up (which itself routes to closing
    // when finished). Never jump straight to closing here, or a job with no role
    // script would skip the Swar AI interview entirely.
    else if (phase === "hr") { setPhaseIdx(0); aiBootRef.current = false; setPhase("ai"); }
    else if (phase === "closing") {
      setEnded(true);
      const transcriptOut = msgs.map((m) => ({ role: m.role, text: m.text }));
      setTimeout(() => onEnd(transcriptOut), 450);
    }
  }, [phase, script]);
  useEffect(() => {
    if (!isScriptedPhase(phase)) return;
    hrIntroDoneRef.current = false;
  }, [phase]);
  useEffect(() => {
    if (phase !== "ai") return;
    // Wait for the candidate to pass the mic check + press Start, so AI-only
    // jobs (which have no scripted questions) still get the pre-start screen.
    if (!interviewStarted) return;
    let cancelled = false;
    (async () => {
      await new Promise((r) => setTimeout(r, 300));
      if (cancelled) return;
      if (aiBootRef.current) return;
      aiBootRef.current = true;
      setBusy(true);
      const bootMsg = "Ask your first role-specific question now. Output only the question.";
      const r0 = await callInterviewClaude(
        [{ role: "user", content: bootMsg }],
        buildSysAi(script),
        applicationId,
      );
      if (cancelled) return;
      const clean = r0.replace("[INTERVIEW_COMPLETE]", "").trim();
      setMsgs((p) => [...p, { role: "ai", text: clean, tag: "ai" }]);
      setHist([{ role: "user", content: bootMsg }, { role: "assistant", content: r0 }]);
      setBusy(false);
      await speak(clean);
      if (!cancelled && !sessionAbortRef.current && !endedRef.current) {
        startAutoListenAfterQuestionRef.current();
      }
    })();
    return () => { cancelled = true; };
  }, [phase, interviewStarted]);
  const startAutoListenAfterQuestion = () => {
    const phoneRec = isPhoneCaptureRef.current ? answerRecRef.current : null;
    if (isPhoneCaptureRef.current ? (!phoneRec || recUnsupportedRef.current) : !recRef.current) return;
    if (endedRef.current || sessionAbortRef.current) return;
    // Resuming an interrupted answer keeps the text already transcribed; the
    // recorder continues the same answer and seq when the questionKey matches.
    const resuming = Boolean(phoneRec) && recResumeKeyRef.current === currentQuestionKeyRef.current;
    if (!resuming) {
      recPartsRef.current = new Map();
      setPartialNotice("");
    }
    recResumeKeyRef.current = null;
    suppressSpeechFinalizeRef.current = false;
    spokenAnswerCommittedRef.current = false;
    hadSpeechRef.current = false;
    intentionalStopRef.current = false;
    accumulatedAnswerRef.current = "";
    sessionFinalRef.current = "";
    autoListenActiveRef.current = true;
    clearTimeout(silenceTimerRef.current);
    silenceTimerRef.current = null;
    clearTimeout(recStartTimerRef.current);
    clearInterval(countdownTimerRef.current);
    setTranscript("");
    txRef.current = "";
    transcriptDisplayRef.current = "";
    setMicError("");
    setCapturing(false);
    setListening(true);

    // The mic stays OFF during "3" and "2". On the final tick ("1") we start the
    // recognizer so it warms up during the last ~0.7s and is genuinely capturing
    // exactly when the countdown hits 0 and the UI says "Speak now" — no dead
    // "connecting…" gap, and the candidate's first words are never clipped.
    const startRecognizer = () => {
      if (endedRef.current || sessionAbortRef.current || !autoListenActiveRef.current) return;
      if (phoneRec) {
        // The recorder owns its own silence detection and 90s cap, so the
        // desktop silenceTimerRef machinery stays idle on phones.
        void phoneRec.start({
          language: langCode,
          applicationId,
          questionKey: currentQuestionKeyRef.current,
          silenceMs: SILENCE_SUBMIT_MS,
          maxMs: 90000,
        });
        return;
      }
      if (!recRef.current) return;
      try {
        recRef.current.start();
      } catch (_) {
        setListening(false);
        autoListenActiveRef.current = false;
      }
    };

    // Visible "start answering in 3, 2, 1" countdown.
    setCountdown(ANSWER_COUNTDOWN_STEPS);
    let n = ANSWER_COUNTDOWN_STEPS;
    countdownTimerRef.current = setInterval(() => {
      n -= 1;
      setCountdown(n);
      if (n === 1) {
        // Last second: warm the mic up so it's live the instant we say "Speak now".
        startRecognizer();
      }
      if (n <= 0) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
    }, ANSWER_COUNTDOWN_TICK_MS);
  };
  startAutoListenAfterQuestionRef.current = startAutoListenAfterQuestion;
  const postScriptedAnswers = async (finalize) => {
    try {
      let attemptId = attemptIdRef.current;
      if (attemptId == null) {
        const sr = await fetch(
          "/api/voice-bot/interview-session-start",
          apiFetchInit({
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              applicationId,
              language: context.language || "English",
            }),
          }),
        );
        if (sr.ok) {
          const sd = await sr.json();
          if (sd.attemptId != null) {
            attemptId = Number(sd.attemptId);
            attemptIdRef.current = attemptId;
          }
        }
      }
      const res = await fetch(
        "/api/voice-bot/interview-answers",
        apiFetchInit({
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            applicationId,
            attemptId: attemptId ?? undefined,
            answers: hrPayloadRef.current,
            finalizeInterview: !!finalize,
            language: context.language || "English",
          }),
        }),
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        console.error("interview-answers save failed:", err.error || res.status);
      } else {
        const body = await res.json().catch(() => ({}));
        if (body.attemptId != null) attemptIdRef.current = Number(body.attemptId);
      }
    } catch (e) { console.error(e); }
  };
  const dispatchAnswer = async (text) => {
    const answer = (text || "").trim();
    if (!answer || busy || ended || sessionAbortRef.current) return;
    accumulatedAnswerRef.current = "";
    sessionFinalRef.current = "";
    setTranscript(""); txRef.current = ""; transcriptDisplayRef.current = ""; setTextInput(""); setMicError("");
    if (isScriptedPhase(phase) && script) {
      const qs = getScriptedList(script, phase);
      if (!qs.length) return;
      const cur = qs[phaseIdx];
      setBusy(true);
      const { answerText, answerTextEn } = await resolveAnswerFields(answer);
      setMsgs((p) => [...p, { role: "user", text: answer, tag: phase }]);
      hrPayloadRef.current.push({
        questionId: cur.id > 0 ? cur.id : null,
        questionText: cur.question,
        answerText,
        answerTextEn,
        audioUrl: null,
        durationSeconds: null,
      });
      const nextIdx = phaseIdx + 1;
      const isLastInPhase = nextIdx >= qs.length;
      try {
        await postScriptedAnswers(isLastInPhase && phase === "closing");
      } catch (e) {
        console.error(e);
      }
      if (nextIdx < qs.length) {
        setPhaseIdx(nextIdx);
        hrIntroDoneRef.current = false;
        setBusy(true);
        const nq = qs[nextIdx];
        let toSpeak = nq.question;
        try {
          toSpeak = await translateHrScriptLine(nq.question);
        } catch (_) {}
        setMsgs((p) => [...p, { role: "ai", text: toSpeak, tag: phase }]);
        setBusy(false);
        await speak(toSpeak);
        if (!sessionAbortRef.current && !endedRef.current) {
          startAutoListenAfterQuestion();
        }
      } else if (phase === "opening") {
        setPhaseIdx(0);
        hrIntroDoneRef.current = false;
        setBusy(false);
        setPhase("hr");
      } else if (phase === "hr") {
        setBusy(false);
        aiBootRef.current = false;
        setPhase("ai");
      } else if (phase === "closing") {
        setBusy(false);
        let transcriptOut = null;
        setMsgs((p) => {
          const next = [...p];
          transcriptOut = next.map((m) => ({ role: m.role, text: m.text }));
          return next;
        });
        setEnded(true);
        setTimeout(() => onEnd(transcriptOut || msgs.map((m) => ({ role: m.role, text: m.text }))), 450);
      }
      return;
    }
    if (phase === "ai") {
      const lastAi = [...msgs].reverse().find((m) => m.role === "ai");
      setBusy(true);
      const { answerText, answerTextEn } = await resolveAnswerFields(answer);
      if (lastAi?.text) {
        hrPayloadRef.current.push({
          questionId: null,
          questionText: lastAi.text,
          answerText,
          answerTextEn,
          audioUrl: null,
          durationSeconds: null,
        });
        try {
          await postScriptedAnswers(false);
        } catch (e) {
          console.error(e);
        }
      }
      const prevAiQCount = aiQCount;
      const h = [...hist, { role: "user", content: answer }];
      // Fetch the next AI follow-up. A genuine connection failure must NOT be
      // treated as a real question or finalize the interview — otherwise a
      // mid-interview network blip would wrongly mark the candidate
      // "Interviewed". Retry once, then bail out to the incomplete/abandon path.
      let r = null;
      try {
        r = await callInterviewClaude(h, buildSysAi(script), applicationId, {
          throwOnError: true,
        });
      } catch (_) {
        try {
          await new Promise((res) => setTimeout(res, 800));
          r = await callInterviewClaude(h, buildSysAi(script), applicationId, {
            throwOnError: true,
          });
        } catch (_) {
          r = null;
        }
      }
      if (r == null) {
        setBusy(false);
        setMicError(
          "Connection lost — your answers are saved. Please rejoin to finish.",
        );
        // Marks the application incomplete_technical (not completed) and exits.
        await safeAbandon("ai_followup_network_error");
        return;
      }
      const done = r.includes("[INTERVIEW_COMPLETE]");
      const clean = r.replace("[INTERVIEW_COMPLETE]", "").trim();
      setHist([...h, { role: "assistant", content: r }]);
      const nextUserTurnCount = prevAiQCount + 1;
      const isFinalTurn = done || nextUserTurnCount >= MAX_AI;
      setBusy(false);
      // Build the next message list synchronously so we don't depend on the
      // setMsgs updater having run (which caused a transcript race on empty turns).
      const newMsgs = [...msgs, { role: "user", text: answer, tag: "ai" }];
      // Only show/speak a real AI question — never a blank/token-only turn.
      if (clean) {
        newMsgs.push({ role: "ai", text: clean, tag: "ai" });
        setAiQCount((c) => c + 1);
      }
      const transcriptOut = newMsgs.map((m) => ({ role: m.role, text: m.text }));
      // Update the on-screen question/counter BEFORE speaking so the displayed
      // question always matches the audio (fixes the "hear Q4 while screen shows
      // Q3" misalignment).
      setMsgs(newMsgs);
      if (clean) await speak(clean);
      if (sessionAbortRef.current || endedRef.current) return;
      if (isFinalTurn) {
        const closingQs = getScriptedList(script, "closing");
        if (closingQs.length) {
          setPhase("closing");
          setPhaseIdx(0);
          hrIntroDoneRef.current = false;
        } else {
          await postScriptedAnswers(true);
          setEnded(true);
          setTimeout(() => onEnd(transcriptOut), 450);
        }
      } else {
        startAutoListenAfterQuestion();
      }
    }
  };
  dispatchAnswerRef.current = dispatchAnswer;
  const finalizeSpokenAnswer = () => {
    if (suppressSpeechFinalizeRef.current || spokenAnswerCommittedRef.current) return;
    if (busyRef.current || endedRef.current || sessionAbortRef.current) return;
    const committed = (txRef.current || "").trim();
    const fallback =
      !committed && hadSpeechRef.current
        ? capAnswer(
            isMobileSttRef.current
              ? accumulatedAnswerRef.current || ""
              : transcriptDisplayRef.current || accumulatedAnswerRef.current || "",
          ).trim()
        : "";
    const answer = committed || fallback;
    if (!answer) {
      setMicError("No speech detected — please speak again.");
      return;
    }
    setMicError("");
    spokenAnswerCommittedRef.current = true;
    void dispatchAnswerRef.current(answer);
  };
  finalizeSpokenAnswerRef.current = finalizeSpokenAnswer;
  // Fully stop the live mic when the candidate chooses to type, so a pending
  // spoken/echo answer can't auto-submit and pre-empt the typed answer.
  const clearCaptureCues = () => {
    clearTimeout(recStartTimerRef.current);
    recStartTimerRef.current = null;
    clearInterval(countdownTimerRef.current);
    countdownTimerRef.current = null;
    setCountdown(0);
    setCapturing(false);
  };
  const stopMicForTyping = () => {
    suppressSpeechFinalizeRef.current = true;
    autoListenActiveRef.current = false;
    spokenAnswerCommittedRef.current = true;
    intentionalStopRef.current = false;
    clearTimeout(silenceTimerRef.current);
    silenceTimerRef.current = null;
    clearCaptureCues();
    setListening(false);
    setTranscript("");
    txRef.current = "";
    transcriptDisplayRef.current = "";
    accumulatedAnswerRef.current = "";
    sessionFinalRef.current = "";
    recResumeKeyRef.current = null;
    answerRecRef.current?.cancel();
    if (recRef.current) {
      try { recRef.current.abort(); } catch (_) {}
    }
  };
  const stopAudioOutput = () => {
    try { if (window.speechSynthesis) window.speechSynthesis.cancel(); } catch (_) {}
    const current = audioRef.current;
    // Also cancels any /api/tts request still in flight, so a clip that has not
    // arrived yet can never start playing after we asked for silence.
    stopServerTts();
    if (current) {
      try { current.src = ""; } catch (_) {}
    }
    setSpeaking(false);
  };
  const releaseMicrophone = () => {
    // Set flags before stop()/abort() so a synchronous onend cannot restart capture.
    suppressSpeechFinalizeRef.current = true;
    autoListenActiveRef.current = false;
    intentionalStopRef.current = true;
    clearTimeout(silenceTimerRef.current);
    silenceTimerRef.current = null;
    clearCaptureCues();
    setListening(false);
    setCapturing(false);
    stopAudioOutput();
    recResumeKeyRef.current = null;
    answerRecRef.current?.cancel();
    const rec = recRef.current;
    if (rec) {
      destroySpeechRecognizer(rec);
      recRef.current = null;
    }
  };
  const stopInterviewSession = () => {
    sessionAbortRef.current = true;
    releaseMicrophone();
  };
  releaseMicrophoneRef.current = releaseMicrophone;
  stopInterviewSessionRef.current = stopInterviewSession;
  const sendAnswer = (text) => {
    stopMicForTyping();
    dispatchAnswer(text);
  };
  const endInterview = async () => {
    stopInterviewSession();
    if (!ended && applicationId && typeof onAbandon === "function") {
      await safeAbandon("candidate_clicked_end");
      return;
    }
    onEnd(msgs);
  };
  const lastQ = [...msgs].reverse().find((m) => m.role === "ai");
  const scriptedQs = isScriptedPhase(phase) ? getScriptedList(script, phase) : [];
  const scriptedTotal = scriptedQs.length;
  const interviewSteps = useMemo(() => {
    if (!script) return [];
    const steps = [];
    if ((script.opening || []).length) steps.push({ key: "opening", label: "Introduction" });
    const roleQs = Array.isArray(script.role) ? script.role : (script.questions || []);
    if (roleQs.length) steps.push({ key: "hr", label: "Role-specific" });
    steps.push({ key: "ai", label: "AIRA follow-up" });
    if ((script.closing || []).length) steps.push({ key: "closing", label: "Closing" });
    return steps;
  }, [script]);
  const currentStepIdx = interviewSteps.findIndex((s) => s.key === phase);
  const approxMinsRemaining = useMemo(() => {
    if (!script || ended || phase === "load" || phase === "err") return null;
    const minsPer = { opening: 2, hr: 2.5, ai: 2, closing: 1.5 };
    const openingLen = (script.opening || []).length;
    const hrLen = (Array.isArray(script.role) ? script.role : (script.questions || [])).length;
    const closingLen = (script.closing || []).length;
    const aiTotal = Math.min(30, Math.max(1, Number(script.aiFollowUpCount) || MAX_AI));
    let mins = 0;
    if (phase === "opening") {
      mins += Math.max(0, openingLen - phaseIdx) * minsPer.opening;
      mins += hrLen * minsPer.hr + aiTotal * minsPer.ai + closingLen * minsPer.closing;
    } else if (phase === "hr") {
      mins += Math.max(0, hrLen - phaseIdx) * minsPer.hr;
      mins += aiTotal * minsPer.ai + closingLen * minsPer.closing;
    } else if (phase === "ai") {
      mins += Math.max(0, aiTotal - aiQCount) * minsPer.ai;
      mins += closingLen * minsPer.closing;
    } else if (phase === "closing") {
      mins += Math.max(0, closingLen - phaseIdx) * minsPer.closing;
    }
    return mins > 0 ? Math.max(1, Math.round(mins)) : null;
  }, [script, phase, phaseIdx, aiQCount, MAX_AI, ended]);
  const phaseLabel = phase === "opening" ? `Introduction · ${Math.min(phaseIdx + 1, scriptedTotal)}/${scriptedTotal || "—"}` : phase === "hr" ? `Role-specific · ${Math.min(phaseIdx + 1, scriptedTotal)}/${scriptedTotal || "—"}` : phase === "closing" ? `Closing · ${Math.min(phaseIdx + 1, scriptedTotal)}/${scriptedTotal || "—"}` : phase === "ai" ? `Follow-up · ${Math.min(aiQCount + 1, MAX_AI)}/${MAX_AI}` : "";
  if (phase === "load") {
    return (
      <div className="min-h-dvh bg-white flex flex-col items-center justify-center p-6 sm:p-8 safe-bottom">
        <div className="w-10 h-10 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin mb-4"/>
        <p className="text-ink-400 text-sm">{!applicationId ? "Resolving application…" : "Loading interview script…"}</p>
      </div>
    );
  }
  if (phase === "err") {
    return (
      <div className="min-h-dvh bg-white flex flex-col items-center justify-center p-6 sm:p-8 text-center safe-bottom">
        <p className="text-red-600 mb-4 text-sm">Could not start interview (missing application or script).</p>
        <button type="button" onClick={() => onEnd([])} className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-ink-600 rounded-xl text-sm font-bold">Exit</button>
      </div>
    );
  }
  // Everything the Start tap must do once the rules are settled. Kept together
  // so the gesture-bound work (AudioContext, fullscreen) still runs inside the
  // user's tap on the modal's button — iOS refuses it otherwise.
  const beginInterview = () => {
    sessionAbortRef.current = false;
    ensureAudioWarm();
    dbgRef.current("Start clicked", dbgSnapshot());
    void answerRecRef.current?.prime();
    if (!isFullscreen()) void enterFullscreen();
    setInterviewStarted(true);
  };

  const acceptRules = async () => {
    setRulesBusy(true);
    setRulesError("");
    try {
      const res = await fetch(
        "/api/voice-bot/interview-rules-ack",
        apiFetchInit({
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            applicationId,
            device: isPhoneCaptureRef.current ? "phone" : "desktop",
          }),
        }),
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Could not save your confirmation");
      }
      // Recorded before starting: if this write fails the candidate sees a retry
      // here rather than entering an interview the backend will refuse.
      setRulesAcked(true);
      setShowRules(false);
      beginInterview();
    } catch (e) {
      setRulesError(e?.message || "Could not save your confirmation. Please try again.");
    } finally {
      setRulesBusy(false);
    }
  };

  const mobileWatermarkActive = isMobileSttRef.current && interviewStarted;
  const progressPct = interviewSteps.length > 0 && currentStepIdx >= 0
    ? Math.round(((currentStepIdx + (ended ? 1 : 0)) / interviewSteps.length) * 100)
    : 0;
  return (
    <div className="min-h-dvh bg-white flex flex-col safe-bottom">
      {mobileWatermarkActive ? (
        <div
          className="fixed inset-0 pointer-events-none z-[9999] overflow-hidden select-none"
          aria-hidden="true"
        >
          {/* Screenshot deterrent, so a leaked capture of the questions carries
              both the brand and the confidentiality notice. content-start used
              to pack every row against the top, which left the lower half of
              the screen bare once scale/rotation pushed the block up; centring
              the rows and raising the tile count + overscan covers the whole
              screen through the -12° rotation. Opacity stays low deliberately:
              the candidate must still be able to read the question underneath. */}
          <div className="absolute inset-0 flex flex-wrap content-center justify-center gap-x-8 gap-y-8 p-6 -rotate-12 scale-150 opacity-[0.07]">
            {Array.from({ length: 60 }).map((_, i) => (
              <span key={i} className="text-slate-900 font-bold whitespace-nowrap leading-none">
                <span className="text-sm tracking-wider">AIRA</span>
                <span className="text-[9px]"> · Indira IVF · Interview in progress</span>
              </span>
            ))}
          </div>
        </div>
      ) : null}
      {dbgOn && isPhoneCaptureRef.current ? ( /* DBG-b7c1 on-screen log panel (phones only) */
        <div className="fixed left-0 right-0 bottom-0 z-[10000] bg-black/90 text-green-300 text-[10px] font-mono leading-tight max-h-[36vh] overflow-y-auto px-2 pb-2 pt-1 select-text">
          <div className="flex items-center justify-between text-white mb-1 sticky top-0 bg-black/90 py-1">
            <span>DBG-b7c1 · {dbgLines.length} lines · flags: {[...dbgFlagsRef.current].join(",") || "none"}</span>
            <button
              type="button"
              className="px-2 py-0.5 bg-white text-black rounded font-bold"
              onClick={() => { try { void navigator.clipboard?.writeText?.(dbgLines.join("\n")); } catch (_) {} }}
            >
              Copy log
            </button>
          </div>
          {dbgLines.map((l, i) => <div key={i}>{l}</div>)}
        </div>
      ) : null}
      <img
        src="/great-place-to-work.png"
        alt="Indira IVF — Great Place To Work Certified, Jan 2026–Jan 2027, India"
        /* Dropped below the header so it cannot cover the "End" button. */
        className="hidden sm:block fixed fixed-safe-below-header w-16 md:w-20 h-auto z-10 drop-shadow-lg pointer-events-none"
        loading="lazy"
      />
      {tabReturnNotice ? (
        <div className="bg-teal-600 text-white text-center text-xs sm:text-sm font-bold px-4 py-2 safe-top" role="status">
          Welcome back — your interview is still running. Stay on this tab until you finish.
        </div>
      ) : null}
      <div className="bg-white border-b border-slate-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2 safe-top">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <div className={`w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center shrink-0 ${speaking ? "animate-pulse" : ""}`}>
            <img src="/aira-logo.png" alt="AIRA" className="w-full h-full object-contain" />
          </div>
          <div className="min-w-0">
            <p className="font-bold text-ink-900 text-xs sm:text-sm truncate">AIRA Voice Interview</p>
            <p className="text-[11px] sm:text-xs text-brand-600 truncate">{context.language} · {phaseLabel}</p>
          </div>
        </div>
        <button type="button" onClick={endInterview} className="shrink-0 px-3 py-2 sm:py-1.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg text-xs font-bold border border-red-200 min-h-[44px] sm:min-h-0">End</button>
      </div>
      {interviewSteps.length > 0 ? (
        <div className="bg-slate-50 border-b border-slate-200 px-4 py-2 sm:py-3">
          <div className="max-w-3xl mx-auto">
            {/* Compact progress on phones */}
            <div className="sm:hidden">
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <p className="text-xs font-bold text-ink-700">
                  Step {Math.max(1, currentStepIdx + 1)} of {interviewSteps.length}
                  {interviewSteps[currentStepIdx] ? ` · ${interviewSteps[currentStepIdx].label}` : ""}
                </p>
                {approxMinsRemaining != null && !ended ? (
                  <p className="text-[11px] text-slate-500 shrink-0">~{approxMinsRemaining} min</p>
                ) : null}
              </div>
              <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                <div className="h-full bg-brand-600 rounded-full transition-all duration-300" style={{ width: `${Math.min(100, Math.max(0, progressPct))}%` }} />
              </div>
            </div>
            {/* Step pills on tablet/desktop */}
            <div className="hidden sm:flex flex-wrap items-center justify-between gap-2">
              <ol className="flex flex-wrap items-center gap-1 text-xs list-none m-0 p-0">
                {interviewSteps.map((step, i) => {
                  const done = currentStepIdx > i;
                  const active = step.key === phase;
                  return (
                    <li key={step.key} className="flex items-center gap-1">
                      {i > 0 ? <span className="text-slate-300" aria-hidden="true">›</span> : null}
                      <span className={`px-2 py-0.5 rounded-full font-bold whitespace-nowrap ${active ? "bg-brand-600 text-white" : done ? "bg-green-100 text-green-700" : "bg-white text-slate-400 border border-slate-200"}`}>
                        {done ? "✓ " : ""}{step.label}
                      </span>
                    </li>
                  );
                })}
              </ol>
              {approxMinsRemaining != null && !ended ? (
                <p className="text-xs text-slate-500 shrink-0">Approx. {approxMinsRemaining} min remaining</p>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
      <div className="flex-1 flex flex-col items-center justify-center px-4 sm:px-6 py-5 sm:py-8 max-w-3xl mx-auto w-full">
        <div className="mb-4 sm:mb-6 relative">
          <div className={`w-28 h-28 sm:w-40 sm:h-40 rounded-full flex items-center justify-center text-white text-4xl sm:text-5xl font-black transition-all duration-500 ${listening ? "bg-gradient-to-br from-red-500 to-rose-600 scale-105 shadow-2xl" : speaking ? "bg-gradient-to-br from-brand-500 to-brand-700 scale-110 shadow-2xl" : ""}`}>{speaking ? "🔊" : listening ? "🎤" : <img src="/aira-logo.png" alt="AIRA" className="w-full h-full object-contain" />}</div>
          {speaking && <div className="absolute inset-0 rounded-full border-4 border-brand-400 animate-ping opacity-40"/>}
          {listening && <div className="absolute inset-0 rounded-full border-4 border-red-400 animate-ping opacity-50"/>}
        </div>
        <div className="text-center mb-6 min-h-[3rem]">
          {busy && <p className="text-brand-600 text-sm animate-pulse">AIRA is thinking…</p>}
          {speaking && !busy && <p className="text-brand-600 text-sm">🔊 AIRA is speaking</p>}
          {listening && (
            countdown > 0
              ? <p className="text-brand-600 text-sm font-bold">Get ready — answer in {countdown}…</p>
              : capturing
                ? <p className="text-red-500 text-sm animate-pulse">🎤 Speak now — I'm listening</p>
                /* Phones: uploads are draining and the mic is already closed —
                   the "Transcribing…" line below owns this moment instead. */
                : isPhoneCaptureRef.current && recStatus === "finishing"
                  ? null
                  : <p className="text-brand-600 text-sm animate-pulse">Connecting microphone…</p>
          )}
          {!busy && !speaking && !listening && !ended && !interviewStarted && (
            <p className="text-ink-400 text-sm max-w-md mx-auto">Press Start once — after each question, speak your answer; it sends after {SILENCE_SUBMIT_MS / 1000}s of silence.</p>
          )}
          {ended && <p className="text-green-600 text-sm">✓ Complete</p>}
          {isPhoneCaptureRef.current && recStatus === "finishing" && !ended ? (
            <p className="text-brand-600 text-sm animate-pulse">Transcribing your answer…</p>
          ) : null}
          {micError && <p className="text-amber-600 text-xs mt-1">{micError}</p>}
          {partialNotice && !ended ? <p className="text-amber-600 text-xs mt-1">{partialNotice}</p> : null}
          {/* Phone capture controls: a live level meter so the candidate can see
              they are being heard, plus an explicit Done as well as the silence
              auto-stop. The MediaRecorder fallback tier emits no levels. */}
          {isPhoneCaptureRef.current && interviewStarted && !ended && capturing ? (
            <div className="mt-3 max-w-xs mx-auto px-2 flex flex-col items-center gap-2">
              {answerRecRef.current?.getTier?.() === "mediarecorder" ? (
                <p className="text-ink-400 text-[11px]">● Recording…</p>
              ) : (
                <AudioLevelMeter level={recLevel} active={recSpeaking} />
              )}
              <button
                type="button"
                onClick={() => answerRecRef.current?.stop()}
                className="px-6 py-2 rounded-full bg-brand-600 hover:bg-brand-700 text-white font-bold text-sm min-h-[44px]"
              >
                Done answering
              </button>
              <p className="text-ink-400 text-[11px]">
                or just stop talking — it sends after {SILENCE_SUBMIT_MS / 1000}s of silence
              </p>
            </div>
          ) : null}
          {isPhoneCaptureRef.current && interviewStarted && !ended && micError && !capturing && !recUnsupportedRef.current && recStatus !== "finishing" ? (
            <button
              type="button"
              onClick={() => { setMicError(""); startAutoListenAfterQuestionRef.current(); }}
              className="mt-2 px-6 py-2 rounded-full bg-brand-600 hover:bg-brand-700 text-white font-bold text-sm min-h-[44px]"
            >
              Retry answer
            </button>
          ) : null}
        </div>
        {lastQ && (
          <div
            className="max-w-xl w-full bg-slate-50 border border-slate-200 rounded-2xl p-5 mb-4 text-center select-none"
            onCopy={(e) => e.preventDefault()}
            onCut={(e) => e.preventDefault()}
            onContextMenu={(e) => e.preventDefault()}
          >
            <p className="text-xs uppercase font-black text-brand-600 mb-2">Question</p>
            <p className="text-ink-900 text-base">{lastQ.text}</p>
          </div>
        )}
        {(transcript || listening) && (
          <div
            className="max-w-xl w-full bg-rose-50 border border-rose-200 rounded-2xl p-4 mb-4 select-none"
            onCopy={(e) => e.preventDefault()}
            onCut={(e) => e.preventDefault()}
            onContextMenu={(e) => e.preventDefault()}
          >
            <p className="text-xs uppercase font-black text-rose-600 mb-1">Your answer</p>
            {transcript ? (
              <p className="text-ink-900">{transcript}</p>
            ) : countdown > 0 ? (
              <div className="flex items-center gap-3">
                <span className="flex-none w-9 h-9 rounded-full bg-brand-600 text-white font-black flex items-center justify-center text-lg">{countdown}</span>
                <span className="text-ink-600 italic">Start answering in {countdown}…</span>
              </div>
            ) : capturing ? (
              <p className="text-rose-600 font-bold italic animate-pulse">Speak now…</p>
            ) : isPhoneCaptureRef.current && recStatus === "finishing" ? (
              <p className="text-ink-400 italic">Transcribing…</p>
            ) : (
              <p className="text-ink-400 italic">Connecting microphone…</p>
            )}
          </div>
        )}
        {!ended && supported && !interviewStarted && (
          <div className="flex flex-col items-center gap-3 my-4">
            <div className="max-w-md w-full bg-brand-50 border border-brand-200 rounded-xl px-4 py-3 flex items-center gap-3">
              <span className="text-xl" aria-hidden="true">🎧</span>
              <p className="text-ink-600 text-sm">
                Please use headphones for a better experience and clearer audio quality — it also prevents echo during your answers.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                // The rules must be accepted before the first interview for this
                // role. Already accepted (or a reattempt of the same role, which
                // reuses the row) goes straight through.
                if (!rulesAlreadyAcked) {
                  setShowRules(true);
                  return;
                }
                beginInterview();
              }}
              disabled={busy}
              className="px-10 py-4 rounded-2xl bg-brand-600 hover:bg-brand-700 disabled:bg-slate-200 disabled:text-slate-400 text-white font-black text-lg shadow-2xl"
            >
              Start interview
            </button>
            {showRules ? (
              <InterviewRulesModal
                isPhone={isPhoneCaptureRef.current}
                busy={rulesBusy}
                error={rulesError}
                onAccept={acceptRules}
                onCancel={() => { if (!rulesBusy) { setShowRules(false); setRulesError(""); } }}
              />
            ) : null}
          </div>
        )}
        {showText && !ended && (
          <div className="max-w-xl w-full mt-4 flex flex-col sm:flex-row gap-2">
            <input
              data-answer-input="true"
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              onKeyDown={(e) => {
                if ((e.ctrlKey || e.metaKey) && (e.key || "").toLowerCase() === "v") {
                  e.preventDefault();
                  return;
                }
                if (e.key === "Enter") sendAnswer(textInput);
              }}
              onPaste={(e) => e.preventDefault()}
              onCopy={(e) => e.preventDefault()}
              onCut={(e) => e.preventDefault()}
              onDrop={(e) => e.preventDefault()}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              placeholder="Type answer…"
              disabled={busy}
              className="flex-1 px-4 py-3 bg-white border border-slate-200 rounded-xl text-ink-900 text-base sm:text-sm focus:outline-none focus:border-brand-600 select-none"
            />
            <button type="button" onClick={() => sendAnswer(textInput)} disabled={!textInput.trim() || busy} className="w-full sm:w-auto px-5 py-3 bg-brand-600 hover:bg-brand-700 text-white font-bold rounded-xl text-sm disabled:bg-slate-200 disabled:text-slate-400 min-h-[44px]">Send</button>
          </div>
        )}
        {supported && !showText && !ended && (!isScriptedPhase(phase) || interviewStarted) && (
          <button type="button" onClick={() => { stopMicForTyping(); setShowText(true); }} className="text-ink-400 hover:text-ink-900 text-xs mt-3 underline">Type instead</button>
        )}
        {!supported && <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center text-amber-800 text-sm max-w-md">⚠ Browser doesn't support voice. Use text.</div>}
      </div>
      {kioskWarning && !ended && kioskSupportedRef.current ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/80 backdrop-blur-sm px-4 sm:px-6 safe-x safe-bottom"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="kiosk-warning-title"
        >
          <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-6 sm:p-8 text-center">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center text-3xl" aria-hidden="true">⚠️</div>
            <h2 id="kiosk-warning-title" className="text-lg font-black text-ink-900 mb-2">
              {isMobileSttRef.current ? "Please stay on this tab" : "Please stay in full-screen"}
            </h2>
            <p className="text-ink-600 text-sm mb-1">
              {isMobileSttRef.current
                ? <>You left the interview view <span className="font-bold">{kioskStrikes}</span> {kioskStrikes === 1 ? "time" : "times"}.</>
                : <>Your interview must run in full-screen. You left full-screen <span className="font-bold">{kioskStrikes}</span> {kioskStrikes === 1 ? "time" : "times"}.</>}
            </p>
            <p className="text-red-600 text-sm font-bold mb-5">
              {KIOSK_MAX_STRIKES - kioskStrikes} {KIOSK_MAX_STRIKES - kioskStrikes === 1 ? "attempt" : "attempts"} left — after that the interview ends and you'll need to request a re-attempt.
            </p>
            <button
              type="button"
              onClick={resumeKiosk}
              className="w-full px-6 py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-white font-black text-base shadow-lg min-h-[44px]"
            >
              {isMobileSttRef.current ? "Return to interview" : "Resume in full-screen"}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

